type Id = string | number;

// Root segments exported so consumers that must compare by root (e.g. the socket backlinks
// predicate in useSyncAuditData) reference a single source of truth instead of bare strings.
export const REQUEST_CONTAINER_ROOT = 'request-container';
export const REQUEST_BLOCK_ROOT = 'request-block';
export const REQUEST_BACKLINKS_ROOT = 'request-container-backlinks';

/**
 * Request-flow query keys (Server-state rebuild, P5). Preserves today's exact values — this
 * is the highest-usage domain, feeds the socket sync layer, and backs optimistic updates, so
 * P5a centralizes the keys WITHOUT reshaping the invalidation graph. The `*Root` prefix
 * helpers name the bare arrays the socket layer used to build inline
 * (`['request-container', auditId]`, `['request-block', auditId, requestId]`).
 *
 * Note the domain spans several roots by design:
 *   - `request-containers` (plural) — the LIST.
 *   - `request-container`  (singular) — a container's detail + activity.
 *   - `request-block`      — block-scoped sub-resources (activity/comments/history).
 *   - `request-container-backlinks` — kept SEPARATE so the frequent container-prefix
 *     invalidation after block edits never refetches the expensive cross-entity backlinks.
 */
export const requestKeys = {
  // ── containers list ['request-containers', …] ───────────────────────────
  list: (auditId?: string) => ['request-containers', auditId] as const,
  /** Every audit's list. `list()` is not that prefix: its `undefined` segment matches no audit. */
  listAll: () => ['request-containers'] as const,

  // ── container detail root ['request-container', …] ──────────────────────
  containerRoot: (auditId?: string) => [REQUEST_CONTAINER_ROOT, auditId] as const,
  /** Every audit's container reads, for a write that is not scoped to one audit. */
  containerAll: () => [REQUEST_CONTAINER_ROOT] as const,
  container: (auditId: string, requestId: Id) =>
    [REQUEST_CONTAINER_ROOT, auditId, String(requestId)] as const,
  containerActivity: (auditId: string, requestId: Id) =>
    [REQUEST_CONTAINER_ROOT, auditId, String(requestId), 'activity'] as const,

  // ── backlinks (separate root, deliberate) ───────────────────────────────
  backlinksRoot: () => [REQUEST_BACKLINKS_ROOT] as const,
  backlinks: (auditId: string, requestId: Id) =>
    [REQUEST_BACKLINKS_ROOT, auditId, String(requestId)] as const,

  // ── block sub-resources root ['request-block', …] ───────────────────────
  blockRoot: (auditId?: string) => [REQUEST_BLOCK_ROOT, auditId] as const,
  blockRootForRequest: (auditId: string, requestId: Id) =>
    [REQUEST_BLOCK_ROOT, auditId, String(requestId)] as const,
  blockActivity: (auditId: string, requestId: Id, blockId: Id) =>
    [REQUEST_BLOCK_ROOT, auditId, String(requestId), String(blockId), 'activity'] as const,
  blockComments: (auditId: string, requestId: Id, blockId: Id) =>
    [REQUEST_BLOCK_ROOT, auditId, String(requestId), String(blockId), 'comments'] as const,
  blockResponseHistory: (auditId: string, requestId: Id, blockId: Id) =>
    [REQUEST_BLOCK_ROOT, auditId, String(requestId), String(blockId), 'response-history'] as const,

  // ── bexio job statuses ──────────────────────────────────────────────────
  bexioRefetchStatus: (auditId: string, targetId: Id) =>
    ['bexio-refetch-status', auditId, String(targetId)] as const,
  bexioExportStatus: (auditId: string, targetId: Id, jobId?: Id | null) =>
    [
      'bexio-export-status',
      auditId,
      String(targetId),
      jobId ? String(jobId) : 'actionable',
    ] as const,
} as const;
